import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/styles/print.css";
import App from "@/App";
import * as Sentry from "@sentry/react";
import RootErrorBoundary from "@/components/RootErrorBoundary";
import { installApiResilience } from "@/services/apiResilience";
import { installChunkFailureHandler } from "@/lib/chunkRecovery";
import { redactString } from "@/lib/redactTokens";
import { resolveEnvironment } from "@/lib/appEnvironment";
import { isClientFacingPath } from "@/lib/publicRoutes";

// Must run before anything fetches — makes every raw /api/ GET in the app
// survive a transient 5xx instead of rendering an empty screen.
installApiResilience();
// ONE chunk-load failure handler for the whole app: a capture-phase error
// listener + an unhandledrejection listener, both funnelling into the single
// decision in lib/chunkRecovery. Installed before React mounts so an early
// chunk failure is still caught.
installChunkFailureHandler();
// Front-end error + performance monitoring. No-ops until REACT_APP_SENTRY_DSN
// is set, so this is safe to ship before the Sentry project exists.
if (process.env.REACT_APP_SENTRY_DSN) {
  // A client portal is its own top-level page load, so the boot path already
  // tells us whether this session is a client on their own link. Full error
  // capture is required there and the effective rate is recorded on every event
  // as `client_sample_rate` so it is provable from the event, not the source.
  const bootClientFacing =
    typeof window !== "undefined" && isClientFacingPath(window.location.pathname);
  const clientSampleRate = bootClientFacing ? 1.0 : 0.1;
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    // Derived from the actual host: gotstaffed.com is "production", the Emergent
    // preview host is "preview", localhost is "development". Never a baked-in
    // build flag — the same image ships to preview and production.
    environment: resolveEnvironment(),
    integrations: [Sentry.browserTracingIntegration()],
    // ERRORS: every one of them. A 0.1 sample rate is what made nine clients
    // hitting a dead code chunk on their own payment links look like noise.
    // Performance traces stay sampled — they are volume, not incidents.
    sampleRate: 1.0,
    tracesSampleRate: 0.1,
    initialScope: {
      // Recorded on every event from this page load, so full capture on a
      // client-facing route can be confirmed from a real event.
      tags: { client_sample_rate: String(clientSampleRate) },
    },
    ignoreErrors: [/ResizeObserver loop/i, "Non-Error promise rejection captured"],
    // A client portal token IS a credential: anyone who can read the error log
    // could open that client's booking. Scrub it out of everything that leaves
    // the browser — url, transaction name, message and breadcrumbs.
    beforeSend(event) {
      try {
        if (event.message) event.message = redactString(event.message);
        if (event.transaction) event.transaction = redactString(event.transaction);
        if (event.request?.url) event.request.url = redactString(event.request.url);
        if (event.tags?.url) event.tags.url = redactString(event.tags.url);
        (event.breadcrumbs || []).forEach((b) => {
          if (b.message) b.message = redactString(b.message);
          ["url", "to", "from"].forEach((k) => {
            if (b.data?.[k]) b.data[k] = redactString(b.data[k]);
          });
        });
      } catch {
        /* scrubbing must never drop the event */
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      try {
        if (breadcrumb.message) breadcrumb.message = redactString(breadcrumb.message);
        ["url", "to", "from"].forEach((k) => {
          if (breadcrumb.data?.[k]) breadcrumb.data[k] = redactString(breadcrumb.data[k]);
        });
      } catch {
        /* noop */
      }
      return breadcrumb;
    },
  });
}

// BLOCK S12 Item 4b — expose the initialised Sentry so the HTML render
// watchdog (a dependency-free inline script) can report "app-did-not-render".
// captureMessage is a safe no-op if Sentry never initialised (no DSN).
if (typeof window !== "undefined") {
  window.Sentry = Sentry;
}

// PWA install prompt fires before React mounts — stash it so the in-app
// "Add to Home Screen" button can trigger it on demand.
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    window.__staffedInstallPrompt = e;
    window.dispatchEvent(new Event("staffed:installready"));
  });
  window.addEventListener("appinstalled", () => {
    window.__staffedInstallPrompt = null;
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </React.StrictMode>,
);
